import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import ContentDetailSheet from "@/components/ContentDetailSheet";
import { Loader2, Plus, Rocket, FileEdit, Trash2, Bot, MousePointerClick, UserCheck } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";

const SEASON_LABEL = { summer: "Verano", fall: "Otoño", winter: "Invierno", spring: "Primavera" };
const STATUS_LABEL = {
  generating: { label: "Generando", cls: "bg-[#F59E0B]/10 text-[#F59E0B]" },
  ready: { label: "Lista", cls: "bg-[#10B981]/10 text-[#10B981]" },
  failed: { label: "Falló", cls: "bg-[#EF4444]/10 text-[#EF4444]" },
  stopped_by_cap: { label: "Detenida por tope", cls: "bg-[#EF4444]/10 text-[#EF4444]" },
};
const CONTENT_TYPE_LABEL = { blog: "Blog", location_page: "Página de ubicación", social_post: "Post social", gmb_post: "Ficha de Google" };

function currentSeason() {
  const month = parseInt(new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", month: "numeric" }).format(new Date()), 10);
  if ([12, 1, 2].includes(month)) return "winter";
  if ([3, 4, 5].includes(month)) return "spring";
  if ([6, 7, 8].includes(month)) return "summer";
  return "fall";
}

export default function CampaignsPage() {
  const { activeTenantId, activeTenant } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [services, setServices] = useState([]);
  const [cities, setCities] = useState([]);
  const [form, setForm] = useState({ service: "", cityKey: "", season: currentSeason() });
  const [expandedId, setExpandedId] = useState(null);
  const [expandedItems, setExpandedItems] = useState([]);
  const [expandedPerf, setExpandedPerf] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [autopilot, setAutopilot] = useState(null);
  const [savingAutopilot, setSavingAutopilot] = useState(false);
  const pollRef = useRef(null);

  const loadAutopilot = useCallback(async () => {
    const res = await api.get("/campaigns/autopilot");
    setAutopilot(res.data);
  }, []);

  const loadPlans = useCallback(async () => {
    const res = await api.get("/campaigns");
    setPlans(res.data);
    return res.data;
  }, []);

  useEffect(() => {
    if (!activeTenantId) return;
    setLoading(true);
    Promise.all([
      loadPlans(),
      api.get("/company"),
      loadAutopilot(),
    ]).then(([, companyRes]) => {
      const c = companyRes.data || {};
      setServices(c.services || []);
      const flat = [];
      for (const area of c.service_areas || []) {
        for (const city of area.cities || []) {
          flat.push({ key: `${city.city}|${area.state}`, city: city.city, state: area.state });
        }
      }
      setCities(flat);
    }).finally(() => setLoading(false));
  }, [activeTenantId, loadPlans, loadAutopilot]);

  useEffect(() => {
    const hasGenerating = plans.some((p) => p.status === "generating");
    if (hasGenerating && !pollRef.current) {
      pollRef.current = setInterval(() => { loadPlans(); }, 3000);
    } else if (!hasGenerating && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [plans, loadPlans]);

  const create = async () => {
    if (!form.service || !form.cityKey) return toast.error("Elegí un servicio y una ciudad");
    const city = cities.find((c) => c.key === form.cityKey);
    setCreating(true);
    try {
      await api.post("/campaigns", { service: form.service, state: city.state, city: city.city, season: form.season === "none" ? null : form.season });
      toast.success("Campaña iniciada: generando las piezas...");
      setOpen(false);
      loadPlans();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear la campaña");
    } finally {
      setCreating(false);
    }
  };

  const toggleExpand = async (plan) => {
    if (expandedId === plan.id) { setExpandedId(null); return; }
    const [detailRes, perfRes] = await Promise.all([
      api.get(`/campaigns/${plan.id}`),
      api.get(`/campaigns/${plan.id}/performance`),
    ]);
    setExpandedItems(detailRes.data.items || []);
    const byPiece = {};
    for (const row of perfRes.data.by_piece || []) byPiece[row.content_item_id] = row;
    setExpandedPerf(byPiece);
    setExpandedId(plan.id);
  };

  const saveAutopilot = async (next) => {
    setSavingAutopilot(true);
    try {
      const res = await api.put("/campaigns/autopilot", next);
      setAutopilot(res.data);
      toast.success("Piloto automático actualizado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar el piloto automático");
    } finally {
      setSavingAutopilot(false);
    }
  };

  const openItem = (item) => { setSelectedItem(item); setSheetOpen(true); };
  const onItemChanged = (updated) => {
    setSelectedItem(updated);
    setExpandedItems((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await api.delete(`/campaigns/${deleteTarget.id}`);
      const { skipped_items } = res.data;
      if (skipped_items?.length) {
        toast.warning(`Campaña borrada. ${skipped_items.length} pieza(s) publicada(s) o en vuelo se conservaron.`);
      } else {
        toast.success("Campaña borrada");
      }
      setExpandedId((id) => (id === deleteTarget.id ? null : id));
      setDeleteTarget(null);
      loadPlans();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo borrar la campaña");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#0055FF]" /></div>;

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#71717A] max-w-xl">
          Un plan completo para un servicio y una ciudad en un solo clic: página de ubicación, blog,
          post social (3 plataformas) y ficha de Google, todo pasando por los 4 chequeos de revisión.
        </p>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-campaign-button" className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md">
              <Plus className="h-4 w-4 mr-1" /> Nueva campaña
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md" data-testid="campaign-create-dialog">
            <DialogHeader><DialogTitle className="font-heading">Nueva campaña</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Servicio</Label>
                <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                  <SelectTrigger data-testid="campaign-service-select"><SelectValue placeholder="Elegir servicio" /></SelectTrigger>
                  <SelectContent>
                    {services.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Ciudad</Label>
                <Select value={form.cityKey} onValueChange={(v) => setForm({ ...form, cityKey: v })}>
                  <SelectTrigger data-testid="campaign-city-select"><SelectValue placeholder="Elegir ciudad" /></SelectTrigger>
                  <SelectContent>
                    {cities.map((c) => <SelectItem key={c.key} value={c.key}>{c.city}, {c.state}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#71717A]">Temporada (opcional, cambia el ángulo del contenido)</Label>
                <Select value={form.season} onValueChange={(v) => setForm({ ...form, season: v })}>
                  <SelectTrigger data-testid="campaign-season-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin temporada (perenne)</SelectItem>
                    {Object.entries(SEASON_LABEL).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                data-testid="create-campaign-submit-button" onClick={create}
                disabled={creating || activeTenant?.status === "suspended"}
                title={activeTenant?.status === "suspended" ? "Cuenta suspendida" : undefined}
                className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md"
              >
                {creating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Rocket className="h-4 w-4 mr-1" />} Crear campaña
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {autopilot && (
        <Card className="rounded-sm border-[#E4E4E7] shadow-none p-4" data-testid="autopilot-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <Bot className="h-5 w-5 text-[#0055FF]" />
              <div>
                <p className="font-heading font-bold text-sm text-[#09090B]">Piloto automático</p>
                <p className="text-xs text-[#71717A]">
                  Genera campañas en borrador desde los huecos de cobertura. Nunca aprueba ni publica solo.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-[#71717A]">Por corrida</Label>
                <Input
                  type="number" min={1} max={5} value={autopilot.campaigns_per_run}
                  onChange={(e) => setAutopilot({ ...autopilot, campaigns_per_run: parseInt(e.target.value, 10) || 1 })}
                  className="h-8 w-16 text-xs" data-testid="autopilot-campaigns-per-run-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-xs text-[#71717A]">Hora local</Label>
                <Select value={String(autopilot.run_hour_local)} onValueChange={(v) => setAutopilot({ ...autopilot, run_hour_local: parseInt(v, 10) })}>
                  <SelectTrigger data-testid="autopilot-hour-select" className="h-8 w-24 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 24 }, (_, h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={autopilot.enabled} onCheckedChange={(v) => setAutopilot({ ...autopilot, enabled: v })} data-testid="autopilot-enabled-switch" />
                <span className="text-xs" data-testid="autopilot-status-label">{autopilot.enabled ? "Activo" : "Apagado"}</span>
              </div>
              <Button
                size="sm" className="bg-[#0055FF] hover:bg-[#0044CC] rounded-md h-8"
                onClick={() => saveAutopilot({ enabled: autopilot.enabled, campaigns_per_run: autopilot.campaigns_per_run, run_hour_local: autopilot.run_hour_local })}
                disabled={savingAutopilot} data-testid="autopilot-save-button"
              >
                {savingAutopilot ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Guardar"}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {plans.length === 0 ? (
        <Card className="rounded-sm border-[#E4E4E7] shadow-none">
          <div className="flex flex-col items-center py-16 text-center">
            <Rocket className="h-8 w-8 text-[#A1A1AA]" />
            <p className="mt-3 text-sm text-[#71717A]">Todavía no creaste ninguna campaña.</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {plans.map((p) => {
            const status = STATUS_LABEL[p.status] || STATUS_LABEL.generating;
            return (
              <Card key={p.id} className="rounded-sm border-[#E4E4E7] shadow-none p-5" data-testid={`campaign-card-${p.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-heading font-bold text-[#09090B]">{p.name}</p>
                    <p className="text-xs text-[#71717A] mt-0.5">
                      {p.season ? SEASON_LABEL[p.season] : "Sin temporada"} · {p.piece_count} piezas
                      <span data-testid={`campaign-performance-${p.id}`}> · {p.clicks ?? 0} clics · {p.leads ?? 0} leads</span>
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={`border-0 text-[10px] uppercase tracking-wider rounded-sm ${status.cls}`}>{status.label}</Badge>
                    <Button
                      variant="ghost" size="icon" className="h-8 w-8 text-[#A1A1AA] hover:text-[#EF4444] hover:bg-[#EF4444]/10"
                      onClick={() => setDeleteTarget(p)} data-testid={`campaign-delete-button-${p.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {p.status === "generating" && <Progress value={p.progress} className="mt-3" data-testid={`campaign-progress-${p.id}`} />}
                {p.error_message && <p className="text-xs text-[#F59E0B] mt-2" data-testid={`campaign-error-${p.id}`}>{p.error_message}</p>}
                {p.status !== "generating" && p.piece_count > 0 && (
                  <Button variant="outline" size="sm" className="mt-3 rounded-md" onClick={() => toggleExpand(p)} data-testid={`campaign-toggle-${p.id}`}>
                    <FileEdit className="h-3.5 w-3.5 mr-1" /> {expandedId === p.id ? "Ocultar piezas" : "Ver piezas"}
                  </Button>
                )}
                {expandedId === p.id && (
                  <div className="mt-3 grid sm:grid-cols-2 gap-2" data-testid={`campaign-items-${p.id}`}>
                    {expandedItems.map((item) => {
                      const perf = expandedPerf[item.id];
                      return (
                        <button key={item.id} onClick={() => openItem(item)} data-testid={`campaign-item-${item.id}`}
                          className="text-left text-xs border border-[#E4E4E7] rounded-md p-2.5 hover:border-[#0055FF] transition-colors">
                          <p className="font-medium truncate">{item.title}</p>
                          <p className="text-[#71717A] mt-0.5">{CONTENT_TYPE_LABEL[item.content_type] || item.content_type} · {item.status}</p>
                          {perf && (perf.clicks > 0 || perf.leads > 0) && (
                            <p className="text-[#0055FF] mt-1 flex items-center gap-2" data-testid={`campaign-item-performance-${item.id}`}>
                              <span className="flex items-center gap-0.5"><MousePointerClick className="h-3 w-3" /> {perf.clicks}</span>
                              <span className="flex items-center gap-0.5"><UserCheck className="h-3 w-3" /> {perf.leads}</span>
                            </p>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <ContentDetailSheet item={selectedItem} open={sheetOpen} onOpenChange={setSheetOpen} onChanged={onItemChanged} />

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="campaign-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Borrar "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Se van a borrar el plan y sus {deleteTarget?.piece_count || 0} pieza(s) de contenido.
              Las piezas ya publicadas o con una publicación en vuelo se conservan y quedan sueltas en Contenido.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="campaign-delete-cancel-button">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete} disabled={deleting}
              className="bg-[#EF4444] hover:bg-[#DC2626]" data-testid="campaign-delete-confirm-button"
            >
              {deleting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Borrar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
